/**
 * /onboard/setup — host route for the first-login chooser + lean wizard.
 *
 * Reached from OnboardShop right after a fresh shop is created (when
 * the `firstLoginV2` feature flag is on). If the user is not signed
 * in or has no active shop, falls back to /auth or /onboard.
 *
 * ONB-1 (K28): re-entry guard. Once the setup wizard has completed for
 * this shop, navigating back to /onboard/setup must NOT re-render the
 * chooser/wizard — re-running the commit duplicates technicians, bays,
 * and parking. A completed shop is redirected to the dashboard. The
 * authorized way to revise setup is the Settings / Team Management /
 * Birdseye surfaces; an explicit `?edit=1` escape hatch is honored for a
 * deliberate re-entry so we never trap a future "edit setup" affordance.
 */

import { useEffect } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { FirstLoginJunction } from '@/components/onboarding/FirstLoginJunction';

const OnboardSetup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, activeShop, isLoading: isAuthLoading, isShopDataLoading } = useAuth();
  const shopId = activeShop?.id;

  // Explicit, authorized re-entry (e.g. a future "edit setup" link).
  const editMode = searchParams.get('edit') === '1';

  // ONB-1: has the setup wizard already completed for this shop? Scoped
  // by shop_id + flow (not user) so a second owner re-entering is caught
  // too where RLS permits; degrades to the current user's own row
  // otherwise. `isLoading` keeps us from flashing the wizard before the
  // answer is known.
  const { data: alreadyCompleted, isLoading: completionLoading } = useQuery({
    queryKey: ['onboarding-setup-completed', shopId],
    queryFn: async () => {
      if (!shopId) return false;
      const { data, error } = await supabase
        .from('onboarding_state')
        .select('completed')
        .eq('shop_id', shopId)
        .eq('flow', 'setup_wizard')
        .eq('completed', true)
        .limit(1)
        .maybeSingle();
      if (error) {
        // Non-fatal: if we can't read completion, allow the wizard
        // (commitWizard's own ONB-1 idempotency guard is the backstop).
        console.warn('[OnboardSetup] onboarding completion read failed (non-fatal):', error);
        return false;
      }
      return !!data?.completed;
    },
    enabled: !!shopId,
    staleTime: 60000,
  });

  // Phase 3 Batch C: BOTH redirects must wait for auth to resolve.
  //
  // OnboardShop finishes shop creation with `window.location.replace('/onboard/setup')`
  // — a FULL PAGE RELOAD. On that fresh load AuthContext starts with user=null
  // and isLoading=true, so an unguarded `!user` check fired immediately and sent
  // the brand-new owner to /auth. /auth sees the live session and renders its
  // "Account Management" view instead of returning them to the app, so the
  // bounce is a dead end: signed in, shop created, parked on the login route.
  //
  // That is what broke the hosted browser suites for three batches. The data
  // path was never at fault — the shop row and its MANAGER membership were
  // created correctly every time (verified directly against QA).
  //
  // No authentication is weakened: a genuinely unauthenticated visitor still
  // goes to /auth, just after auth has actually resolved rather than before.
  useEffect(() => {
    if (isAuthLoading) return;
    if (!user) {
      navigate('/auth', { replace: true });
      return;
    }
    if (!isShopDataLoading && !activeShop) {
      navigate('/onboard', { replace: true });
    }
  }, [user, isAuthLoading, activeShop, isShopDataLoading, navigate]);

  if (!user || !activeShop) {
    return null;
  }

  // Wait for the completion check before deciding what to render so we
  // never momentarily show the wizard to a completed shop.
  if (completionLoading) {
    return null;
  }

  if (alreadyCompleted && !editMode) {
    return <Navigate to="/" replace />;
  }

  return <FirstLoginJunction />;
};

export default OnboardSetup;
